/**
 * The cache writes that keep the reads in this folder current.
 *
 * They live together rather than beside the query each one writes to because a
 * single change fans out across several: a device that changes has to be
 * written to the device itself and to the devices list behind every screen.
 * Actions call these after a write; the queries call the `addOrUpdate*Cache`
 * ones to seed from a read.
 */
import { QueryClient } from '@tanstack/react-query';
import { Beacon, Device } from '@/api';
import {
    FETCH_BEACON_QUERY_KEY,
    FETCH_BEACONS_QUERY_KEY,
    FETCH_DEVICE_QUERY_KEY,
    FETCH_DEVICES_QUERY_KEY
} from './devices-query-keys';

/** Seeds the per-device cache from a read. */
export function addOrUpdateDeviceCache(
    queryClient: QueryClient,
    device: Device
): void {
    queryClient.setQueryData<Device>(
        [FETCH_DEVICE_QUERY_KEY, device.id],
        () => device
    );
}

/**
 * The same as `addOrUpdateDeviceCache`, plus the list write that only a
 * mutation warrants — use this one when a device has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */
export function addOrUpdateAllDevicesCache(
    queryClient: QueryClient,
    device: Device
): void {
    addOrUpdateDeviceCache(queryClient, device);

    queryClient.setQueryData<Device[]>(
        [FETCH_DEVICES_QUERY_KEY],
        (oldDevices) => {
            if (!oldDevices) {
                return [device];
            }

            let didUpdate = false;
            const updatedDevices = oldDevices.map((oldDevice) => {
                if (oldDevice.id === device.id) {
                    didUpdate = true;
                    return device;
                }

                return oldDevice;
            });

            if (!didUpdate) {
                updatedDevices.push(device);
            }

            return [...updatedDevices];
        }
    );
}

/** Drops a device that no longer exists, and the list entry for it. */
export function deleteDeviceFromCache(
    queryClient: QueryClient,
    deviceId: string
): void {
    queryClient.removeQueries({
        queryKey: [FETCH_DEVICE_QUERY_KEY, deviceId]
    });

    queryClient.setQueryData<Device[]>(
        [FETCH_DEVICES_QUERY_KEY],
        (oldDevices) => {
            if (!oldDevices) {
                return [];
            }

            return oldDevices.filter((device) => device.id !== deviceId);
        }
    );
}

/** Seeds the per-beacon cache from a read. */
export function addOrUpdateBeaconCache(
    queryClient: QueryClient,
    beacon: Beacon
): void {
    queryClient.setQueryData<Beacon>(
        [FETCH_BEACON_QUERY_KEY, beacon.id],
        () => beacon
    );
}

/**
 * The same as `addOrUpdateBeaconCache`, plus the list write that only a
 * mutation warrants — use this one when a beacon has actually changed, and the
 * singular one when simply seeding the cache from a read.
 */
export function addOrUpdateAllBeaconsCache(
    queryClient: QueryClient,
    beacon: Beacon
): void {
    addOrUpdateBeaconCache(queryClient, beacon);

    // The beacons lists are keyed by company, but a `Beacon` carries no company
    // id, so the list a new beacon belongs to can only be identified as the one
    // the viewer is looking at. Writing to the active queries alone keeps a new
    // beacon out of another company's list left behind in the cache.
    queryClient.setQueriesData<Beacon[]>(
        { queryKey: [FETCH_BEACONS_QUERY_KEY], type: 'active' },
        (oldBeacons) => {
            if (!oldBeacons) {
                return [beacon];
            }

            let didUpdate = false;
            const updatedBeacons = oldBeacons.map((oldBeacon) => {
                if (oldBeacon.id === beacon.id) {
                    didUpdate = true;
                    return beacon;
                }

                return oldBeacon;
            });

            if (!didUpdate) {
                updatedBeacons.push(beacon);
            }

            return [...updatedBeacons];
        }
    );
}

/** Drops a deleted beacon, and the list entry for it. */
export function deleteBeaconFromCache(
    queryClient: QueryClient,
    beaconId: string
): void {
    queryClient.removeQueries({
        queryKey: [FETCH_BEACON_QUERY_KEY, beaconId]
    });

    // Removing by id is safe across every cached company list — ids are unique,
    // so the lists that never held this beacon are left as they were.
    queryClient.setQueriesData<Beacon[]>(
        { queryKey: [FETCH_BEACONS_QUERY_KEY] },
        (oldBeacons) => {
            if (!oldBeacons) {
                return [];
            }

            return oldBeacons.filter((beacon) => beacon.id !== beaconId);
        }
    );
}
